import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as fieldDefinitionsApi from '../api/fieldDefinitions';

export function AdminFieldDefinitions() {
  const { programId } = useParams();
  const { selectedOrg } = useOrg();
  const { isAdmin } = usePermissions();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterAppliesTo, setFilterAppliesTo] = useState('');
  const [form, setForm] = useState({
    fieldName: '',
    fieldType: 'string',
    label: '',
    description: '',
    required: false,
    appliesTo: 'site',
    validations: {}
  });

  const loadFields = useCallback(async () => {
    if (!programId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (filterAppliesTo) filters.appliesTo = filterAppliesTo;
      const data = await fieldDefinitionsApi.getFieldDefinitions(programId, filters);
      setFields(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [programId, filterAppliesTo]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const handleOpenCreate = () => {
    setEditingField(null);
    setForm({
      fieldName: '',
      fieldType: 'string',
      label: '',
      description: '',
      required: false,
      appliesTo: 'site',
      validations: {}
    });
    setShowModal(true);
  };

  const handleOpenEdit = (field) => {
    setEditingField(field);
    setForm({
      fieldName: field.fieldName,
      fieldType: field.fieldType,
      label: field.label,
      description: field.description || '',
      required: field.required,
      appliesTo: field.appliesTo,
      validations: field.validations || {}
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    try {
      if (editingField) {
        await fieldDefinitionsApi.updateFieldDefinition(selectedOrg.id, programId, editingField.id, {
          label: form.label,
          description: form.description,
          required: form.required,
          validations: form.validations
        });
      } else {
        await fieldDefinitionsApi.createFieldDefinition(selectedOrg.id, programId, form);
      }
      setShowModal(false);
      loadFields();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleValidationChange = (key, value) => {
    setForm(prev => ({
      ...prev,
      validations: {
        ...prev.validations,
        [key]: value
      }
    }));
  };

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to manage field definitions.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Field Definitions"
        subtitle={`Configure custom fields for ${programId}`}
        breadcrumbs={[
          { label: 'Programs', href: '/admin/programs' },
          { label: 'Field Definitions' }
        ]}
      />

      <div style={styles.toolbar}>
        <select
          value={filterAppliesTo}
          onChange={(e) => setFilterAppliesTo(e.target.value)}
          style={styles.select}
          data-testid="applies-to-filter"
        >
          <option value="">All Scopes</option>
          <option value="site">Site</option>
          <option value="asset">Asset</option>
        </select>
        <button
          onClick={handleOpenCreate}
          style={styles.createButton}
          data-testid="create-field-button"
        >
          + Add Field
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading field definitions...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="fields-table">
            <thead>
              <tr>
                <th style={styles.th}>Field Name</th>
                <th style={styles.th}>Label</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Applies To</th>
                <th style={styles.th}>Required</th>
                <th style={styles.th}>Validations</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(field => (
                <tr key={field.id} style={styles.tr} data-testid={`field-${field.id}`}>
                  <td style={styles.td}>
                    <code style={styles.fieldName}>{field.fieldName}</code>
                  </td>
                  <td style={styles.td}>{field.label}</td>
                  <td style={styles.td}>
                    <span style={styles.typeBadge}>{field.fieldType}</span>
                  </td>
                  <td style={styles.td}>{field.appliesTo}</td>
                  <td style={styles.td}>
                    {field.required ? (
                      <span style={styles.requiredBadge}>Required</span>
                    ) : (
                      <span style={styles.optionalBadge}>Optional</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {renderValidations(field.validations)}
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleOpenEdit(field)}
                      style={styles.editButton}
                      data-testid={`edit-${field.id}`}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {fields.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>
                    No field definitions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="field-modal">
            <h3 style={styles.modalTitle}>
              {editingField ? 'Edit Field Definition' : 'New Field Definition'}
            </h3>
            {!editingField && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Field Name</label>
                  <input
                    type="text"
                    value={form.fieldName}
                    onChange={(e) => setForm(prev => ({ ...prev, fieldName: e.target.value }))}
                    style={styles.input}
                    placeholder="e.g., grantAmount"
                    data-testid="field-name-input"
                  />
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Type</label>
                    <select
                      value={form.fieldType}
                      onChange={(e) => setForm(prev => ({ ...prev, fieldType: e.target.value }))}
                      style={styles.select}
                      data-testid="field-type-input"
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="date">Date</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Applies To</label>
                    <select
                      value={form.appliesTo}
                      onChange={(e) => setForm(prev => ({ ...prev, appliesTo: e.target.value }))}
                      style={styles.select}
                      data-testid="field-applies-to-input"
                    >
                      <option value="site">Site</option>
                      <option value="asset">Asset</option>
                    </select>
                  </div>
                </div>
              </>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>Label</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm(prev => ({ ...prev, label: e.target.value }))}
                style={styles.input}
                placeholder="e.g., Grant Amount ($)"
                data-testid="field-label-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                style={styles.input}
                placeholder="Optional description"
                data-testid="field-description-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={form.required}
                  onChange={(e) => setForm(prev => ({ ...prev, required: e.target.checked }))}
                  data-testid="field-required-input"
                />
                Required field
              </label>
            </div>
            <h4 style={styles.sectionTitle}>Validations</h4>
            {(form.fieldType === 'string' || !editingField) && form.fieldType === 'string' && (
              <>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Min Length</label>
                    <input
                      type="number"
                      value={form.validations.minLength || ''}
                      onChange={(e) => handleValidationChange('minLength', parseInt(e.target.value) || undefined)}
                      style={styles.input}
                      data-testid="validation-min-length"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Max Length</label>
                    <input
                      type="number"
                      value={form.validations.maxLength || ''}
                      onChange={(e) => handleValidationChange('maxLength', parseInt(e.target.value) || undefined)}
                      style={styles.input}
                      data-testid="validation-max-length"
                    />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Pattern (Regex)</label>
                  <input
                    type="text"
                    value={form.validations.pattern || ''}
                    onChange={(e) => handleValidationChange('pattern', e.target.value || undefined)}
                    style={styles.input}
                    placeholder="e.g., ^[A-Z0-9-]+$"
                    data-testid="validation-pattern"
                  />
                </div>
              </>
            )}
            {(form.fieldType === 'number' || !editingField) && form.fieldType === 'number' && (
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Min Value</label>
                  <input
                    type="number"
                    value={form.validations.min ?? ''}
                    onChange={(e) => handleValidationChange('min', parseFloat(e.target.value) || undefined)}
                    style={styles.input}
                    data-testid="validation-min"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Value</label>
                  <input
                    type="number"
                    value={form.validations.max ?? ''}
                    onChange={(e) => handleValidationChange('max', parseFloat(e.target.value) || undefined)}
                    style={styles.input}
                    data-testid="validation-max"
                  />
                </div>
              </div>
            )}
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowModal(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.label || (!editingField && !form.fieldName)}
                style={styles.submitButton}
                data-testid="save-field-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function renderValidations(validations) {
  if (!validations || Object.keys(validations).length === 0) {
    return <span style={{ color: '#a0aec0' }}>None</span>;
  }
  
  const parts = [];
  if (validations.min !== undefined) parts.push(`min: ${validations.min}`);
  if (validations.max !== undefined) parts.push(`max: ${validations.max}`);
  if (validations.minLength !== undefined) parts.push(`minLen: ${validations.minLength}`);
  if (validations.maxLength !== undefined) parts.push(`maxLen: ${validations.maxLength}`);
  if (validations.pattern) parts.push(`pattern`);
  
  return <span style={{ fontSize: '0.75rem', color: '#718096' }}>{parts.join(', ')}</span>;
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  createButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  fieldName: {
    background: '#edf2f7',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem'
  },
  typeBadge: {
    padding: '0.25rem 0.5rem',
    background: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '0.75rem',
    textTransform: 'capitalize'
  },
  requiredBadge: {
    padding: '0.25rem 0.5rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '4px',
    fontSize: '0.75rem'
  },
  optionalBadge: {
    padding: '0.25rem 0.5rem',
    background: '#c6f6d5',
    color: '#276749',
    borderRadius: '4px',
    fontSize: '0.75rem'
  },
  editButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #3182ce',
    background: 'white',
    color: '#3182ce',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  sectionTitle: {
    margin: '1.5rem 0 1rem 0',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#4a5568'
  },
  formGroup: {
    marginBottom: '1rem',
    flex: 1
  },
  formRow: {
    display: 'flex',
    gap: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#4a5568'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
